"use client";

import { ActiveNavDecorations } from "@/components/decorations/ActiveNavDecorations";
import { getActiveNavSlime, getActiveSiteLogo } from "@/components/decorations/active-theme";
import { AnnouncementBar } from "@/components/layout/announcement-bar";
import { HeaderDesktopNav } from "@/components/layout/header-desktop-nav";
import { HeaderMobileNav } from "@/components/layout/header-mobile-nav";
import { HeaderSearch } from "@/components/layout/header-search";
import { SiteLogo } from "@/components/layout/site-logo";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { useHeaderAuth } from "@/hooks/use-header-auth";
import { useMobileMenu } from "@/hooks/use-mobile-menu";
import { useScrollHeader } from "@/hooks/use-scroll-header";
import { ANNOUNCEMENT_ENABLED } from "@/lib/constants/announcement";
import { Z_INDEX_CLASSES } from "@/lib/constants/z-index";
import { List, X } from "@phosphor-icons/react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Suspense, useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";

function shouldShowHeaderSearch(pathname: string | null): boolean {
  // Search the games library from anywhere on the public site. Admin runs its own
  // shell/layout, so the games search bar stays out of it.
  const isAdminRoute = pathname?.startsWith("/admin") ?? false;
  return !isAdminRoute;
}

const NAV_SLIME_HIDDEN_PATHS = new Set(["/about", "/educators", "/partners"]);

// skipTrailingSlashRedirect keeps "/about/" reachable, so compare without the slash.
function shouldShowNavSlime(pathname: string | null): boolean {
  if (!pathname) return true;
  return !NAV_SLIME_HIDDEN_PATHS.has(pathname.replace(/\/+$/, ""));
}

export function Header() {
  const pathname = usePathname();
  const { user, isAdmin, isLoading, handleLogout } = useHeaderAuth();
  const { isHeaderVisible, resetHeaderVisibility } = useScrollHeader({ mobileOnly: true });
  const { isMenuOpen, setIsMenuOpen, menuButtonRef, menuContainerRef, isClient } = useMobileMenu({
    isLoading,
    user,
    isAdmin,
  });
  const [isMenuAnimating, setIsMenuAnimating] = useState(false);
  // Track each search instance separately (both stay mounted across breakpoints);
  // the logo collapses when either is expanded.
  const [isMobileSearchActive, setIsMobileSearchActive] = useState(false);
  const [isDesktopSearchActive, setIsDesktopSearchActive] = useState(false);
  const isSearchActive = isMobileSearchActive || isDesktopSearchActive;

  const showSearch = shouldShowHeaderSearch(pathname);

  // Reset header visibility when page changes
  useEffect(() => {
    resetHeaderVisibility();
  }, [pathname, resetHeaderVisibility]);

  // Handle menu slide-in animation
  useEffect(() => {
    if (isMenuOpen) {
      // Start animation after mount
      const timer = setTimeout(() => {
        setIsMenuAnimating(true);
      }, 10);
      return () => clearTimeout(timer);
    }
    // Reset animation state when menu closes
    setIsMenuAnimating(false);
  }, [isMenuOpen]);

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user || !user.email) return "U";
    return user.email.charAt(0).toUpperCase();
  };

  const logo = getActiveSiteLogo();

  const copyLogoAsSvg = useCallback(async () => {
    try {
      const res = await fetch(logo.src);
      if (!res.ok) {
        toast.error(`Failed to load logo (${res.status})`);
        return;
      }
      const svg = await res.text();
      const trimmed = svg.trimStart();
      const head = trimmed.toLowerCase();
      const looksLikeSvg =
        head.startsWith("<svg") ||
        (head.startsWith("<?xml") && /<svg[\s>/]/i.test(trimmed.slice(0, 4096)));
      if (!looksLikeSvg) {
        toast.error("Logo file was not valid SVG");
        return;
      }
      await navigator.clipboard.writeText(svg);
      toast.success("Logo copied as SVG");
    } catch {
      toast.error("Failed to copy logo");
    }
  }, [logo.src]);

  return (
    <>
      {/* Skip Navigation Link */}
      <a
        href="#main-content"
        className="sr-only focus-visible:not-sr-only focus-visible:fixed focus-visible:top-4 focus-visible:left-4 focus-visible:z-[100] focus-visible:px-4 focus-visible:py-2 focus-visible:bg-white focus-visible:text-black focus-visible:rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        Skip to main content
      </a>
      {/*
        Stack the announcement above fixed page backdrops (e.g. ParallaxSpaceBg at z-0).
        Keep the sticky header outside this short wrapper so sticky positioning is not
        constrained by the announcement block's height.
      */}
      {ANNOUNCEMENT_ENABLED ? (
        <div className="relative z-10">
          <AnnouncementBar />
        </div>
      ) : null}
      <header
        id="site-header"
        className={`sticky h-16 top-0 z-50 bg-black text-white shadow-sm transition-transform duration-300 ${
          isHeaderVisible ? "transform-none" : "-translate-y-full"
        } motion-reduce:transition-none`}
      >
        <div className="container mx-auto px-4 py-3 flex justify-between items-center relative gap-2">
          {/* Logo - hidden on mobile when search is active */}
          <ContextMenu>
            <ContextMenuTrigger asChild>
              <Link
                href="/"
                aria-label="PlayMath.org home"
                className={`flex items-center gap-4 relative z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded shrink-0 overflow-hidden transition-all duration-300 ease-in-out motion-reduce:transition-none ${
                  isSearchActive
                    ? "max-w-0 opacity-0 pointer-events-none"
                    : "max-w-[260px] opacity-100"
                }`}
              >
                {/* Left nav decoration */}
                <ActiveNavDecorations placement="left" />
                <SiteLogo />
              </Link>
            </ContextMenuTrigger>
            <ContextMenuContent className="w-56">
              <ContextMenuItem onClick={copyLogoAsSvg}>Copy logo as SVG</ContextMenuItem>
              <ContextMenuItem asChild>
                <a
                  href="/resources/PlayMath.org%20Brand%20Assets.zip"
                  download="PlayMath.org Brand Assets.zip"
                >
                  Download brand assets
                </a>
              </ContextMenuItem>
              <ContextMenuSeparator />
              <ContextMenuItem asChild>
                <Link href="/brand">Visit brand guidelines</Link>
              </ContextMenuItem>
            </ContextMenuContent>
          </ContextMenu>

          {/* Spacer to push nav to the right - hidden on mobile when search is active */}
          <div className={`flex-1 ${isSearchActive ? "md:block hidden" : "block"}`} />

          {/* Desktop Navigation with Search */}
          <div className="hidden md:flex items-center gap-4 shrink-0">
            {/* Search - shown on games pages and topic pages with search enabled */}
            {showSearch && (
              <Suspense fallback={<div className="w-10 h-10" />}>
                <HeaderSearch onActiveChange={setIsDesktopSearchActive} />
              </Suspense>
            )}
            <HeaderDesktopNav
              user={user}
              isAdmin={isAdmin}
              isLoading={isLoading}
              handleLogout={handleLogout}
              getUserInitials={getUserInitials}
            />
          </div>

          {/* Mobile Search - shown on games pages and topic pages with search enabled */}
          {showSearch && (
            <div className="md:hidden flex-1 mx-2 min-w-0">
              <Suspense fallback={<div className="w-full h-10" />}>
                <HeaderSearch onActiveChange={setIsMobileSearchActive} />
              </Suspense>
            </div>
          )}

          {/* Mobile Menu Button */}
          <button
            ref={menuButtonRef}
            className="md:hidden z-20 p-2 text-white hover:text-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded shrink-0 transition-all duration-200"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? (
              <X weight="bold" className="h-6 w-6" aria-hidden="true" />
            ) : (
              <List weight="bold" className="h-6 w-6" aria-hidden="true" />
            )}
          </button>

          {/* Mobile Menu Overlay & Slide-out via Portal (avoid transformed ancestor issues) */}
          {isClient &&
            isMenuOpen &&
            createPortal(
              <>
                <button
                  type="button"
                  tabIndex={-1}
                  className={`fixed inset-0 bg-black bg-opacity-50 ${Z_INDEX_CLASSES.MOBILE_MENU_OVERLAY} transition-opacity duration-300 md:hidden motion-reduce:transition-none border-0 p-0 cursor-pointer opacity-100`}
                  onClick={() => setIsMenuOpen(false)}
                  aria-label="Close menu"
                />

                <div
                  ref={menuContainerRef}
                  className={`fixed top-0 right-0 h-full w-3/4 max-w-xs bg-black text-white ${Z_INDEX_CLASSES.MOBILE_MENU} shadow-xl transform-gpu transition-transform duration-300 ease-in-out md:hidden overflow-x-hidden motion-reduce:transition-none ${
                    isMenuAnimating ? "translate-x-0" : "translate-x-full"
                  }`}
                  style={{ contain: "layout paint size" }}
                  // biome-ignore lint/a11y/useSemanticElements: Using div for custom positioning since native dialog has default centering behavior that conflicts with right-aligned sidebar
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="mobile-menu-title"
                >
                  <div className="flex flex-col h-full pt-16 pb-6 px-6 overflow-x-hidden">
                    <h2 id="mobile-menu-title" className="sr-only">
                      Navigation menu
                    </h2>
                    <HeaderMobileNav
                      user={user}
                      isAdmin={isAdmin}
                      isLoading={isLoading}
                      handleLogout={handleLogout}
                      getUserInitials={getUserInitials}
                    />
                  </div>
                </div>
              </>,
              document.body
            )}
        </div>
      </header>
      {shouldShowNavSlime(pathname) ? <NavSlime headerVisible={isHeaderVisible} /> : null}
    </>
  );
}

/**
 * In flow under the nav, so the announcement bar is part of the starting offset.
 * It scrolls with the page. The negative margins cancel its height so following
 * content does not move.
 * z-0 sits under the nav (z-50) and under z-10 page content such as the banner.
 * Follows the nav's mobile hide transform (h-16) so the drips stay attached.
 */
function NavSlime({ headerVisible }: { headerVisible: boolean }) {
  const slime = getActiveNavSlime();
  if (!slime) return null;

  return (
    <div
      aria-hidden="true"
      data-testid="nav-slime"
      className={`pointer-events-none relative z-0 w-full transition-transform duration-300 motion-reduce:transition-none ${
        headerVisible ? "transform-none" : "-translate-y-16"
      }`}
      style={{
        height: slime.height,
        marginTop: -slime.tuck,
        marginBottom: -(slime.height - slime.tuck),
        backgroundImage: `url("${slime.src}")`,
        backgroundRepeat: "repeat-x",
        backgroundPosition: "left top",
        backgroundSize: `auto ${slime.height}px`,
      }}
    />
  );
}
